import React from "react";
import { useHistory } from "react-router-dom";
import { ArrowRight, Check, CreditCard } from "lucide-react";

const PAYMENT_METHODS = [
  { title: "Mobile Money", description: "Pay via mobile money" },
  { title: "Card", description: "Pay via card" },
];

export function EatPayment() {
  return (
    <section className="relative overflow-hidden bg-[#012d6e] py-20">
      <div className="pointer-events-none absolute right-0 top-0 h-[400px] w-[500px] rounded-full bg-[#017bef]/[0.15] blur-[64px]" />

      <div className="relative mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-16 px-[16px] sm:px-8 lg:grid-cols-2">
        <div>
          <span className="inline-block rounded-full border-[1px] border-solid border-white/[0.15] bg-white/10 px-[16px] py-1.5 text-[12px] font-semibold uppercase leading-4 tracking-[1.2px] text-white/70">
            Payment
          </span>
          <h2 className="pt-[12px] text-[36px] font-bold leading-[1.2] tracking-[-1.2px] text-white sm:text-[48px] sm:leading-[48px]">
            Pay Securely, Without Leaving SPACIA
          </h2>
          <p className="pt-[16px] text-[18px] leading-[29.25px] text-white/60">
            Pay securely, the same way you already do on SPACIA. Secure mobile money and card payments, built into the
            same experience.
          </p>
          <div className="grid grid-cols-1 gap-[16px] pt-8 sm:grid-cols-2">
            {PAYMENT_METHODS.map(method => (
              <div
                key={method.title}
                className="rounded-2xl border-[1px] border-solid border-white/[0.12] bg-white/[0.08] p-[20px]"
              >
                <span className="flex size-9 items-center justify-center rounded-[14px] bg-white/10">
                  <CreditCard className="size-4 text-white" />
                </span>
                <p className="pt-[12px] text-[14px] font-bold leading-5 text-white">{method.title}</p>
                <p className="pt-0.5 text-[12px] leading-4 text-white/45">{method.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-3xl border-[1px] border-solid border-white/[0.12] bg-white/[0.08] p-7">
          <p className="text-[14px] font-bold uppercase leading-5 tracking-[1.4px] text-white">Order Summary</p>
          <div className="pt-[20px]">
            {["Item one", "Item two"].map(item => (
              <div
                key={item}
                className="flex items-center justify-between border-b-[1px] border-solid border-white/[0.08] py-2.5"
              >
                <span className="text-[14px] leading-5 text-white/70">{item}</span>
                <span className="h-1.5 w-16 rounded-full bg-white/10" />
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between pt-6">
            <span className="text-[14px] font-bold leading-5 text-white">Total</span>
            <span className="h-2 w-20 rounded-full bg-white/[0.15]" />
          </div>
          {/* Illustration of the app, so this is not a real action */}
          <p
            aria-hidden
            className="mt-6 w-full rounded-2xl bg-[#017bef] py-[16px] text-center text-[14px] font-bold leading-5 text-white drop-shadow-[0_2px_10px_rgba(1,123,239,0.3)]"
          >
            Place Order
          </p>
          <p className="pt-[12px] text-center text-[12px] leading-4 text-white/30">Secure payment within SPACIA</p>
        </div>
      </div>
    </section>
  );
}

const PLACES = [
  {
    label: "WORK",
    title: "At Work",
    description: "Food delivered to a workspace.",
    image: "/figma-assets/eat/at-work.jpg",
  },
  {
    label: "STAY",
    title: "On a Stay",
    description: "Food delivered to your accommodation.",
    image: "/figma-assets/eat/on-stay.jpg",
  },
  {
    label: "MOVE",
    title: "Between Plans",
    description: "Order food while moving between activities.",
    image: "/figma-assets/eat/between-plans.jpg",
  },
];

export function EatEverywhere() {
  return (
    <section className="bg-white py-20">
      <div className="mx-auto max-w-[1440px] px-[16px] sm:px-8">
        <div className="flex flex-col items-center text-center">
          <p className="text-[14px] font-semibold uppercase leading-5 tracking-[1.4px] text-[#017bef]">
            Everywhere You Are
          </p>
          <h2 className="pt-[12px] text-[36px] font-bold leading-[1.2] tracking-[-1.2px] text-[#0a0a0a] sm:text-[48px] sm:leading-[48px]">
            Eat Wherever Your Day Takes You
          </h2>
          <p className="max-w-[576px] pt-[16px] text-[18px] leading-7 text-[#717182]">
            Enjoy a meal wherever your day takes you — at work, on a stay or between plans.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 pt-14 lg:grid-cols-3">
          {PLACES.map(place => (
            <div key={place.title} className="relative min-h-[360px] overflow-hidden rounded-3xl">
              <img src={place.image} alt={place.title} className="absolute inset-0 size-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[rgba(10,10,10,0.8)] via-[rgba(10,10,10,0.2)] to-transparent" />
              <span className="absolute left-[20px] top-[23px] rounded-full bg-[#017bef] px-[12px] py-[3px] text-[10px] font-bold leading-[15px] tracking-[1px] text-white">
                {place.label}
              </span>
              <div className="absolute inset-x-6 bottom-6">
                <p className="text-[18px] font-bold leading-7 text-white">{place.title}</p>
                <p className="pt-1 text-[14px] leading-5 text-white/60">{place.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const PARTNER_STEPS = [
  { title: "Receive Order", description: "Orders arrive directly through SPACIA." },
  { title: "Prepare", description: "Prepare the order for pickup or delivery." },
  { title: "Pickup / Delivery", description: "Ready for collection or rider handoff." },
  { title: "Customer Receives", description: "Delivered to the user on SPACIA." },
];

const PARTNER_POINTS = [
  { title: "SPACIA User Base", description: "Reach users already booking Work, Stay and Play." },
  { title: "One Platform", description: "No separate app needed — you list directly on SPACIA." },
  { title: "Connected Experience", description: "Part of a user's wider SPACIA journey." },
];

export function EatPartner() {
  const history = useHistory();

  return (
    <section className="bg-white py-20">
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-16 px-[16px] sm:px-8 lg:grid-cols-2">
        <img
          src="/figma-assets/eat/kitchen.jpg"
          alt="Restaurant kitchen"
          className="h-[360px] w-full rounded-3xl object-cover sm:h-[492px]"
        />
        <div>
          <p className="text-[14px] font-semibold uppercase leading-5 tracking-[1.4px] text-[#017bef]">For Partners</p>
          <h2 className="pt-[12px] text-[36px] font-bold leading-[1.2] tracking-[-1.2px] text-[#0a0a0a] sm:text-[48px] sm:leading-[48px]">
            For Restaurant Partners
          </h2>
          <p className="pt-[20px] text-[18px] leading-[29.25px] text-[#717182]">
            Restaurants on SPACIA Eat run a concise pickup and delivery operation, reaching SPACIA's growing user base
            directly through the app they already use to book their workspace, stay and experiences.
          </p>

          <ol className="space-y-[12px] pt-8">
            {PARTNER_STEPS.map((step, index) => (
              <li
                key={step.title}
                className="flex items-start gap-[16px] rounded-2xl border-[1px] border-solid border-[#e5e7eb] bg-[#f8fbff] px-[20px] py-[16px]"
              >
                <span className="pt-0.5 text-[12px] font-bold leading-4 tracking-[1.2px] text-[#017bef]">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <div>
                  <p className="text-[14px] font-semibold leading-5 text-[#0a0a0a]">{step.title}</p>
                  <p className="text-[12px] leading-4 text-[#717182]">{step.description}</p>
                </div>
              </li>
            ))}
          </ol>

          <ul className="space-y-[12px] pt-8">
            {PARTNER_POINTS.map(point => (
              <li key={point.title} className="flex items-start gap-[12px] text-[14px] leading-5">
                <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full bg-[#017bef]/10">
                  <Check className="size-2.5 text-[#017bef]" />
                </span>
                <span>
                  <span className="font-semibold text-[#0a0a0a]">{point.title} — </span>
                  <span className="text-[#717182]">{point.description}</span>
                </span>
              </li>
            ))}
          </ul>

          <button
            type="button"
            onClick={() => history.push("/partner-signup")}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#017bef] px-7 py-3.5 text-[14px] font-bold leading-5 text-white drop-shadow-[0_2px_8px_rgba(1,123,239,0.2)] transition-colors hover:bg-[#0062c0]"
          >
            List Your Restaurant
            <ArrowRight className="size-[15px]" />
          </button>
        </div>
      </div>
    </section>
  );
}
